// ComponentDetailsPanel.js - Technical Card Style Component Details Panel

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import useStore from '../../store/useStore';
import THEME from '../../styles/theme';
import DataSourceBadge from '../DataSourceBadge';
import VoltageSelector from '../VoltageSelector';
import { getAvailableDataSources, getCurrentDataSource } from '../../utils/dataSourceHelpers';

const ComponentDetailsPanel = () => {
  const {
    isComponentDetailsPanelOpen,
    isComponentDetailsPanelPinned,
    selectedNodeId,
    nodes,
    calculationResults,
    closeComponentDetailsPanel,
    toggleComponentDetailsPanelPin,
    updateNodeData,
    componentLibrary
  } = useStore();

  const [localParams, setLocalParams] = useState({});
  const [copiedValue, setCopiedValue] = useState(null);

  const selectedNode = nodes.find(n => n.id === selectedNodeId);
  const nodeResults = selectedNodeId ? calculationResults[selectedNodeId] : null;

  // Update local params when selected node changes
  useEffect(() => {
    if (selectedNode && selectedNode.data.parameters) {
      setLocalParams(JSON.parse(JSON.stringify(selectedNode.data.parameters)));
    }
  }, [selectedNode]);

  if (!isComponentDetailsPanelOpen) return null;

  const handleClose = () => {
    closeComponentDetailsPanel();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isComponentDetailsPanelPinned) {
      handleClose();
    }
  };

  const handleTogglePin = () => {
    toggleComponentDetailsPanelPin();
  };

  const handleParamChange = (paramKey, value) => {
    setLocalParams(prev => ({
      ...prev,
      [paramKey]: {
        ...prev[paramKey],
        value: parseFloat(value) || 0
      }
    }));
  };

  const handleParamBlur = (paramKey) => {
    if (!selectedNode) return;

    const param = localParams[paramKey];
    const numValue = parseFloat(param.value);

    // Validation
    if (isNaN(numValue)) {
      setLocalParams(JSON.parse(JSON.stringify(selectedNode.data.parameters)));
      return;
    }

    // Check min/max constraints
    if (param.min !== undefined && numValue < param.min) {
      alert(`Value must be at least ${param.min}`);
      setLocalParams(JSON.parse(JSON.stringify(selectedNode.data.parameters)));
      return;
    }
    if (param.max !== undefined && numValue > param.max) {
      alert(`Value must be at most ${param.max}`);
      setLocalParams(JSON.parse(JSON.stringify(selectedNode.data.parameters)));
      return;
    }

    // Save to store
    updateNodeData(selectedNodeId, { parameters: localParams });
  };

  const handleCopyValue = (value, label) => {
    navigator.clipboard.writeText(value.toString());
    setCopiedValue(label);
    setTimeout(() => setCopiedValue(null), 1500);
  };

  // Get compression state color for header
  const getHeaderColor = () => {
    if (!nodeResults || !nodeResults.compressionState) return '#FFFFFF';
    if (nodeResults.compressionState === 'compressed') return '#FFA500'; // Orange
    if (nodeResults.compressionState === 'warning') return '#FFD700'; // Yellow
    return '#FFFFFF'; // White
  };

  // Render empty state
  if (!selectedNode) {
    return (
      <>
        <Backdrop onClick={handleBackdropClick} isPinned={isComponentDetailsPanelPinned} />
        <Panel>
          <HeaderButtons>
            <PinButton onClick={handleTogglePin} isPinned={isComponentDetailsPanelPinned}>
              {isComponentDetailsPanelPinned ? '📌' : '📍'}
            </PinButton>
            <CloseButton onClick={handleClose}>✕</CloseButton>
          </HeaderButtons>
          <EmptyMessage>Double-click a component to view details</EmptyMessage>
        </Panel>
      </>
    );
  }

  return (
    <>
      <Backdrop onClick={handleBackdropClick} isPinned={isComponentDetailsPanelPinned} />
      <Panel>
        <HeaderButtons>
          <PinButton onClick={handleTogglePin} isPinned={isComponentDetailsPanelPinned}>
            {isComponentDetailsPanelPinned ? '📌' : '📍'}
          </PinButton>
          <CloseButton onClick={handleClose}>✕</CloseButton>
        </HeaderButtons>

        {/* Component Header - Like Node Card */}
        <Header bgColor={getHeaderColor()}>
          <HeaderMain>{selectedNode.data.name || selectedNode.type}</HeaderMain>
          <HeaderSub>{selectedNode.type}</HeaderSub>
          {selectedNode.data.partNumber && (
            <PartNumber>{selectedNode.data.partNumber}</PartNumber>
          )}
        </Header>

        {/* Symbol Watermark - Like Node Card */}
        <SymbolWatermark>
          <svg width="160" height="160" viewBox="0 0 160 160" fill="none">
            <path
              d="M40 80 L60 80 M100 80 L120 80 M70 60 L70 100 M90 60 L90 100 M80 50 L80 110"
              stroke="#ff6b6b"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.15"
            />
          </svg>
        </SymbolWatermark>

        <Content>
          {/* S2P Data Section */}
          {selectedNode && selectedNode.data?.parameters?.hasS2PData?.value && (
            <Section>
              <SectionTitle>S2P DATA</SectionTitle>
              <S2PInfo>
                <S2PStatusRow>
                  <S2PLabel>Status:</S2PLabel>
                  <S2PStatus>
                    📊 S2P File Available
                  </S2PStatus>
                </S2PStatusRow>
                {selectedNode.data?.parameters?.s2pData?.value && (
                  <S2PDetailsRow>
                    <S2PDetail>
                      Range: {Math.round(selectedNode.data.parameters.s2pData.value.freqMin || 0)} - {Math.round(selectedNode.data.parameters.s2pData.value.freqMax || 0)} MHz
                    </S2PDetail>
                    <S2PDetail>
                      Points: {selectedNode.data.parameters.s2pData.value.numPoints || 0}
                    </S2PDetail>
                  </S2PDetailsRow>
                )}
                <S2PInfoText>
                  S21 magnitude data will be used for frequency-dependent gain when "S2P" data source is selected.
                </S2PInfoText>
              </S2PInfo>
            </Section>
          )}

          {/* Voltage Selector Section */}
          {selectedNode && selectedNode.data?.parameters?.hasFreqData?.value && (
            <Section>
              <SectionTitle>FREQUENCY DATA</SectionTitle>
              <ParamsGrid>
                <VoltageSelector node={selectedNode} />
              </ParamsGrid>
            </Section>
          )}

          {/* Parameters Section */}
          {Object.values(localParams || {}).some(p => p.displayOnCard) && (
            <Section>
              <SectionTitle>PARAMETERS</SectionTitle>
              <ParamsGrid>
                {Object.entries(localParams || {}).map(([key, param]) => {
                  // Check if this parameter supports data source selection
                  const paramNameMap = {
                    'gain': 'gain',
                    'noiseFigure': 'noiseFigure',
                    'p1db': 'p1db',
                    'oip3': 'oip3'
                  };
                  const paramName = paramNameMap[key];
                  const supportsDataSource = paramName !== undefined;

                  return param.displayOnCard && (
                    <ParamRow key={key}>
                      <ParamLabel>{param.name}</ParamLabel>
                      <ParamInputWrapper>
                        <ParamInput
                          type="number"
                          value={param.value}
                          onChange={(e) => handleParamChange(key, e.target.value)}
                          onBlur={() => handleParamBlur(key)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.target.blur();
                            }
                          }}
                          min={param.min}
                          max={param.max}
                          step={param.min !== undefined && param.max !== undefined
                            ? (param.max - param.min) / 100
                            : 0.1
                          }
                          disabled={supportsDataSource && getCurrentDataSource(selectedNode, paramName) !== 'manual'}
                        />
                        {param.unit && <Unit>{param.unit}</Unit>}
                        {supportsDataSource && (
                          <DataSourceBadge
                            nodeId={selectedNode.id}
                            paramName={paramName}
                            currentSource={getCurrentDataSource(selectedNode, paramName)}
                            availableSources={getAvailableDataSources(selectedNode, paramName, componentLibrary)}
                          />
                        )}
                      </ParamInputWrapper>
                    </ParamRow>
                  );
                })}
              </ParamsGrid>
            </Section>
          )}

          {/* Results Section */}
          {nodeResults && (
            <Section>
              <SectionTitle>CALCULATED RESULTS</SectionTitle>
              <ResultsGrid>
                <ResultRow onClick={() => handleCopyValue(nodeResults.inputPower, 'Input Power')}>
                  <Label>Input Power</Label>
                  <Value>
                    {nodeResults.inputPower} dBm
                    {copiedValue === 'Input Power' && <CopyCheck>✓</CopyCheck>}
                  </Value>
                </ResultRow>
                <ResultRow onClick={() => handleCopyValue(nodeResults.outputPower, 'Output Power')}>
                  <Label>Output Power</Label>
                  <Value>
                    {nodeResults.outputPower} dBm
                    {copiedValue === 'Output Power' && <CopyCheck>✓</CopyCheck>}
                  </Value>
                </ResultRow>
                <ResultRow onClick={() => handleCopyValue(nodeResults.gain, 'Gain')}>
                  <Label>Gain</Label>
                  <Value>
                    {nodeResults.gain} dB
                    {copiedValue === 'Gain' && <CopyCheck>✓</CopyCheck>}
                  </Value>
                </ResultRow>
                <ResultRow onClick={() => handleCopyValue(nodeResults.cumulativeGain, 'Cumulative Gain')}>
                  <Label>Cumulative Gain</Label>
                  <Value>
                    {nodeResults.cumulativeGain} dB
                    {copiedValue === 'Cumulative Gain' && <CopyCheck>✓</CopyCheck>}
                  </Value>
                </ResultRow>
                <ResultRow onClick={() => handleCopyValue(nodeResults.cumulativeNF, 'Cumulative NF')}>
                  <Label>Cumulative NF</Label>
                  <Value>
                    {nodeResults.cumulativeNF} dB
                    {copiedValue === 'Cumulative NF' && <CopyCheck>✓</CopyCheck>}
                  </Value>
                </ResultRow>
                {nodeResults.compressionState && (
                  <ResultRow>
                    <Label>Compression</Label>
                    <StatusValue state={nodeResults.compressionState}>
                      {nodeResults.compressionState === 'compressed'
                        ? 'Compressed'
                        : nodeResults.compressionState === 'warning'
                          ? 'Warning'
                          : 'Normal'}
                    </StatusValue>
                  </ResultRow>
                )}
              </ResultsGrid>
            </Section>
          )}

          {/* System Summary */}
          {nodeResults && calculationResults.systemSummary && (
            <Section>
              <SectionTitle>SYSTEM SUMMARY</SectionTitle>
              <ResultsGrid>
                <ResultRow onClick={() => handleCopyValue(calculationResults.systemSummary.totalGain, 'Total Gain')}>
                  <Label>Total Gain</Label>
                  <Value>
                    {calculationResults.systemSummary.totalGain} dB
                    {copiedValue === 'Total Gain' && <CopyCheck>✓</CopyCheck>}
                  </Value>
                </ResultRow>
                <ResultRow onClick={() => handleCopyValue(calculationResults.systemSummary.systemNF, 'System NF')}>
                  <Label>System NF</Label>
                  <Value>
                    {calculationResults.systemSummary.systemNF} dB
                    {copiedValue === 'System NF' && <CopyCheck>✓</CopyCheck>}
                  </Value>
                </ResultRow>
                <ResultRow onClick={() => handleCopyValue(calculationResults.systemSummary.outputPower, 'Output Power')}>
                  <Label>Output Power</Label>
                  <Value>
                    {calculationResults.systemSummary.outputPower} dBm
                    {copiedValue === 'Output Power' && <CopyCheck>✓</CopyCheck>}
                  </Value>
                </ResultRow>
                <ResultRow onClick={() => handleCopyValue(calculationResults.systemSummary.systemOIP3, 'System OIP3')}>
                  <Label>System OIP3</Label>
                  <Value>
                    {calculationResults.systemSummary.systemOIP3} dBm
                    {copiedValue === 'System OIP3' && <CopyCheck>✓</CopyCheck>}
                  </Value>
                </ResultRow>
              </ResultsGrid>
            </Section>
          )}
        </Content>
      </Panel>
    </>
  );
};

// Styled Components
const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.isPinned ? 'transparent' : 'rgba(0, 0, 0, 0.3)'};
  z-index: ${THEME.zIndex.panel - 1};
  pointer-events: ${props => props.isPinned ? 'none' : 'auto'};
  animation: fadeIn 200ms ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Panel = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 320px;
  max-height: 80vh;
  background: ${THEME.cardBackground};
  border: 2px solid #000000;
  border-left: 3px solid ${THEME.accent};
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.6);
  z-index: ${THEME.zIndex.panel};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideIn 250ms cubic-bezier(0.34, 1.2, 0.64, 1);

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  &:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.7);
  }
`;

const HeaderButtons = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
  z-index: 10;
`;

const PinButton = styled.button`
  background: ${props => props.isPinned ? 'rgba(220, 20, 60, 0.2)' : 'rgba(0, 0, 0, 0.3)'};
  color: ${props => props.isPinned ? THEME.accent : THEME.textSecondary};
  border: none;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  padding: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${THEME.transitionFast};
  border-radius: 4px;

  &:hover {
    background: rgba(220, 20, 60, 0.3);
    color: ${THEME.accent};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const CloseButton = styled.button`
  background: rgba(0, 0, 0, 0.3);
  color: ${THEME.textSecondary};
  border: none;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${THEME.transitionFast};
  border-radius: 4px;

  &:hover {
    background: rgba(220, 20, 60, 0.2);
    color: ${THEME.accent};
  }

  &:active {
    transform: scale(0.95);
  }
`;

// Header matching node card style
const Header = styled.div`
  padding: 4px 12px;
  background: ${props => props.bgColor};
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: relative;
  z-index: 5;
`;

const HeaderMain = styled.div`
  color: rgba(0, 0, 0, 0.85);
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const HeaderSub = styled.div`
  color: rgba(0, 0, 0, 0.6);
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const PartNumber = styled.div`
  color: #ff6b6b;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  margin-top: 2px;
`;

// Symbol Watermark - like node cards
const SymbolWatermark = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 1;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  z-index: 2;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(220, 20, 60, 0.4);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(220, 20, 60, 0.6);
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  &:not(:last-child) {
    padding-bottom: 8px;
    border-bottom: 1px solid ${THEME.borderColor};
  }
`;

const SectionTitle = styled.div`
  color: ${THEME.textPrimary};
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 2px;
  opacity: 0.6;
`;

const ParamsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ParamRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const ParamLabel = styled.div`
  color: ${THEME.textSecondary};
  font-size: 13px;
  font-weight: 500;
  flex-shrink: 0;
`;

const ParamInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  justify-content: flex-end;
`;

const ParamInput = styled.input`
  background: ${THEME.inputBackground};
  color: ${THEME.textPrimary};
  border: 1px solid ${THEME.inputBorder};
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 14px;
  font-weight: 600;
  transition: all ${THEME.transitionFast};
  width: 70px;
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${THEME.accent};
    background: #000000;
    box-shadow: 0 0 0 1px ${THEME.accent};
  }

  &:hover {
    border-color: ${THEME.inputBorderFocus};
  }

  /* Remove spinner arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const Unit = styled.span`
  color: ${THEME.textSecondary};
  font-size: 12px;
  font-weight: 500;
`;

const ResultsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 0;
  cursor: ${props => props.onClick ? 'pointer' : 'default'};
  transition: all ${THEME.transitionFast};
  border-top: none;

  &:hover {
    background: ${props => props.onClick ? 'rgba(220, 20, 60, 0.08)' : 'transparent'};
  }

  &:active {
    transform: ${props => props.onClick ? 'scale(0.98)' : 'none'};
  }
`;

const Label = styled.div`
  color: ${THEME.textSecondary};
  font-size: 13px;
  font-weight: 500;
`;

const Value = styled.div`
  color: ${THEME.textPrimary};
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusValue = styled.div`
  color: ${props =>
    props.state === 'compressed' ? THEME.error :
    props.state === 'warning' ? THEME.warning :
    THEME.success
  };
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
`;

const CopyCheck = styled.span`
  color: ${THEME.success};
  font-size: 12px;
  animation: fadeInOut 1500ms ease;

  @keyframes fadeInOut {
    0%, 100% { opacity: 0; transform: scale(0.8); }
    20%, 80% { opacity: 1; transform: scale(1); }
  }
`;

const EmptyMessage = styled.div`
  color: ${THEME.textMuted};
  font-size: 12px;
  text-align: center;
  padding: ${THEME.spacing.xxl};
  line-height: 1.6;
`;

// S2P Info Styles
const S2PInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const S2PStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const S2PLabel = styled.div`
  color: ${THEME.textSecondary};
  font-size: 12px;
  font-weight: 500;
`;

const S2PStatus = styled.div`
  color: ${THEME.accent};
  font-size: 13px;
  font-weight: 600;
`;

const S2PDetailsRow = styled.div`
  display: flex;
  gap: 16px;
  padding: 8px 12px;
  background: rgba(59, 130, 246, 0.05);
  border-radius: 4px;
  border: 1px solid rgba(59, 130, 246, 0.2);
`;

const S2PDetail = styled.div`
  color: ${THEME.textSecondary};
  font-size: 11px;
  font-weight: 500;
`;

const S2PInfoText = styled.div`
  color: ${THEME.textMuted};
  font-size: 11px;
  line-height: 1.5;
  font-style: italic;
`;

export default ComponentDetailsPanel;
